import type { ReactElement } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Hourglass } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/hooks/use-auth'
import { canViewPortalAs, currentPortalView } from '@/lib/portal-view'
import { GENESIS_HOME } from '@/lib/roles'
import { signInPathFor } from '@/lib/safe-next'

/**
 * PortalRoute — a guarda de `/portal/*` (F1 do portal do cliente).
 *
 * É a metade que fecha a porta pelo lado de dentro; a outra metade vive no
 * `ProtectedRoute`, que manda o cliente para cá quando ele aterra numa tela
 * interna. As duas juntas dizem a mesma frase que o §4 da spec impõe no
 * servidor: **cada papel só existe do seu lado da fronteira.**
 *
 *   • a carregar          → esqueleto (NFR-DS-2: sem spinners);
 *   • sem sessão          → /sign-in?next=<caminho+query>, para voltar aqui;
 *   • papel ≠ 'customer'  → a dashboard interna, SALVO numa vista do portal;
 *   • 'customer'          → renderiza o portal, SALVO a conta ainda sem empresa
 *                           (`awaitingCustomer`, Story 35.5) ou sem um gestor do
 *                           cliente ativo (`awaitingManager`, Story 38.7): essas veem
 *                           o aviso.
 *
 * A VISTA DA GENESIS (issue #584; Story 35.9): uma pessoa da Genesis com perfil de
 * cliente, ou gestora de conta de clientes, entra quando a ABA escolheu uma vista que
 * ela pode ter (`lib/portal-view.ts`). O `requirePortalUser` confere o mesmo, e recusa
 * também o cliente inativo — isto só evita aterrar num portal que ia dar 403.
 *
 * ⚠️ ESTA ÁRVORE NÃO PODE SER EMBRULHADA NO `ProtectedRoute`. Ele redireciona
 * `role='customer'` para `/portal`; embrulhar `/portal` nele daria um ciclo de
 * redirects. É por isso que a verificação de sessão está repetida aqui em vez
 * de reutilizada por composição — a repetição são quatro linhas, o ciclo seria
 * uma tela branca a piscar.
 *
 * ⚠️ E, como no `admin-route.tsx`: isto é UX, não é o perímetro. Quem recusa
 * dados a quem não é do portal é o `requirePortalUser` da API. Uma sessão da
 * Genesis que force `/portal` no browser não vê dado nenhum de cliente — vê o
 * redirect daqui e, se o contornasse, um 403 da rede.
 *
 * Porquê mandar a Genesis para a dashboard e não mostrar um "Sem acesso" como o
 * `AdminRoute` faz: são situações diferentes. Um recruiter em `/settings/users`
 * pediu algo que talvez devesse poder pedir — a página explica-lhe porquê não.
 * Um recruiter em `/portal` está simplesmente na casa errada: não há nada a
 * explicar, há um caminho a corrigir.
 */
export function PortalRoute({ children }: { children: ReactElement }): ReactElement {
  const { user, isLoading, signOut } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center p-8" aria-busy="true">
        <div className="w-full max-w-2xl space-y-4">
          <Skeleton className="h-12 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to={signInPathFor(location)} replace />
  }

  if (user.role !== 'customer' && !canViewPortalAs(user.portalViews, currentPortalView())) {
    return <Navigate to={GENESIS_HOME} replace />
  }

  if (user.role === 'customer' && user.awaitingCustomer === true) {
    return (
      <AwaitingCustomer
        title="Aguardando o vínculo com a empresa"
        text="Sua conta Genesis tem acesso ao portal do cliente, mas ainda não foi ligada a uma empresa. Um administrador do Caliber faz o vínculo; depois disso, o portal abre aqui."
        onSignOut={() => void signOut()}
      />
    )
  }

  // Story 38.7: sem um gestor do cliente ativo ligado à conta, o portal recusa-a (o
  // `requirePortalUser` dá 403 em cada pedido). A pessoa lê o que falta.
  if (user.role === 'customer' && user.awaitingManager === true) {
    return (
      <AwaitingCustomer
        title="A sua conta ainda não tem acesso ao portal"
        text="Peça a quem o convidou na Genesis."
        onSignOut={() => void signOut()}
      />
    )
  }

  return children
}

/**
 * A conta `customer` que ainda não entra no portal, com o título e o texto do caso:
 *   • sem empresa (Story 35.5, Epic 35 §6) — a pessoa da Genesis com o papel de cliente
 *     no `gms-auth`, até um admin a ligar a um cliente em `/settings/users`;
 *   • sem gestor (Story 38.7) — a conta de um cliente sem um gestor do cliente ativo
 *     ligado a ela, até um admin a ligar a um no painel do gestor.
 * Nos dois, o portal daria 403 em cada pedido (o `requirePortalUser` recusa-a); aqui a
 * pessoa lê o porquê e o que falta.
 *
 * O «Sair» está aqui porque esta tela não tem o menu do portal, e sem ele a pessoa
 * ficava presa. É a mesma forma do «Sem acesso» do `AdminRoute`: um estado, uma frase,
 * uma saída.
 */
function AwaitingCustomer({
  title,
  text,
  onSignOut,
}: {
  title: string
  text: string
  onSignOut: () => void
}): ReactElement {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
      <div className="bg-sunk border-hair text-ink-2 mb-6 flex size-14 items-center justify-center rounded-full border">
        <Hourglass className="size-6" aria-hidden="true" />
      </div>
      <h1 className="text-ink mb-2 text-2xl font-semibold tracking-tight">{title}</h1>
      <p className="text-ink-2 max-w-sm text-sm">{text}</p>
      <Button variant="secondary" className="mt-6" onClick={onSignOut}>
        Sair
      </Button>
    </div>
  )
}

export default PortalRoute
